import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  Button,
  Icon
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { makeStyles } from '@material-ui/core/styles';
import { getAssetManagerImagesApi } from 'api';
import { assetManagerUrl, shopManagerApi } from 'configs';


const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
    <path d="M13.125 9.875V12.375C13.125 12.7065 12.9933 13.0245 12.7589 13.2589C12.5245 13.4933 12.2065 13.625 11.875 13.625H3.125C2.79348 13.625 2.47554 13.4933 2.24112 13.2589C2.0067 13.0245 1.875 12.7065 1.875 12.375V9.875" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.375 6.75L7.5 9.875L10.625 6.75" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.5 9.875V2.375" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const useStyles = makeStyles(({palette}) => ({
  root: {
    height: '100%'
  },
  closeBtn: {
    cursor: 'pointer',
    color: palette.text.secondary,

    '&:hover': {
      color: palette.text.primary
    }
  },
  imageListItem: {
    position: 'relative',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },

    '& .mask': {
      visibility: 'hidden',
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      background:
      'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, ' +
      'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.1) 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-evenly',

    },

    '&:hover .mask': {
      visibility: 'visible'
    }
  },
}));

const ImageGalleryDialog = ({
  title,
  open,
  setOpen,
  onImageSelect,
  imageTagId,
  cols = 3,
  rowHeight,
  isDownloaded = false,
  isPrivate = false,
  selectButtonTitle = 'Select'
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);

  const {
    assetImagesData,
    loadingAssetImagesData
  } = useSelector((state) => state.asset_manager);

  const {items = [], limit, page, total} = assetImagesData[imageTagId]
    ? assetImagesData[imageTagId]
    : {items: [], limit: (cols * 5), page: currentPage, total: 10};

  useEffect(() => {
    getAssetManagerImagesApi({
      dispatch,
      limit: (cols * 5),
      page: currentPage,
      tagIds: imageTagId,
      isPrivate
    });
  }, [imageTagId, currentPage]);

  const handleDownload = async (urlPath) => {
    const url = `${shopManagerApi}/assets/image?url=${assetManagerUrl}/${urlPath}`;

    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.click();
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="image-gallery-dialog"
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id="image-gallery-dialog">
        <div className="flex items-center justify-between">
          <span>{title}</span>
          <Icon
            className={classes.closeBtn}
            onClick={() => setOpen(false)}
            title="Close"
          >
            cancel
          </Icon>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={classes.root}>
          {!loadingAssetImagesData &&
            items.length &&
            <ImageList rowHeight={rowHeight} cols={cols}>
              {items.map((item) => (
                <ImageListItem
                  key={item.id}
                  className={classes.imageListItem}
                >
                  <img src={`${assetManagerUrl}/${item.webPath}`} alt={item.title} />
                  <div className="mask">
                    <Button
                      fullWidth
                      variant="contained"
                      className="bg-white border-radius-8 max-w-80full text-12 py-1"
                      onClick={() => {
                        onImageSelect(item);
                        setOpen(false);
                      }}
                    >
                      <span className="flex items-center">
                        <Icon className="mr-2 text-16">check</Icon>
                        <span>{selectButtonTitle}</span>
                      </span>
                    </Button>
                    {isDownloaded &&
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        className="border-radius-8 max-w-80full text-12 py-1"
                        onClick={() => handleDownload(item.webPath)}
                      >
                        <DownloadIcon />
                        <span className="ml-2">Download to your workspace </span>
                      </Button>
                    }
                  </div>
                </ImageListItem>
              ))}
            </ImageList>
          }
        </div>
        <div className="flex justify-center my-4">
          <Pagination
            page={page}
            count={Math.ceil(Number((total / limit)))}
            shape="rounded"
            onChange={(_, page) => setCurrentPage(page)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGalleryDialog;
